import { computed, ref, toValue, watch, type MaybeRefOrGetter } from "vue";
import type { AvailableAgent, ChatSurface } from "../types/chat";

/**
 * Fetches the list of agents available for the admin chat at a given context path.
 * Returns the built-in default agent plus any custom prompt agents found in the
 * context-path ancestry.
 *
 * Only meaningful for the admin surface — candidate agent resolution is server-only.
 */
export function useChatAgentPicker(
  surface: ChatSurface,
  siteId: MaybeRefOrGetter<string | undefined>,
  contextPath: MaybeRefOrGetter<string | undefined>,
) {
  const { apiUrl, authHeaders } = useChatHttp();

  const agents = ref<AvailableAgent[]>([]);
  const selectedAgentPath = ref<string | null>(null);
  const loading = ref(false);

  let seq = 0;

  async function fetchAgents(): Promise<void> {
    if (surface !== "admin") {
      agents.value = [];
      return;
    }

    const site = toValue(siteId);
    if (!site || !import.meta.client) {
      return;
    }

    const mine = ++seq;
    loading.value = true;

    try {
      const ctx = toValue(contextPath);
      const query = ctx ? `?contextPath=${encodeURIComponent(ctx)}` : "";
      const result = await $fetch<AvailableAgent[]>(
        `${apiUrl()}/v1/chat/admin/agents${query}`,
        { headers: authHeaders(site) },
      );

      if (mine !== seq) return;
      agents.value = result;

      // If the user hasn't made a selection yet, keep null (= auto-resolve).
      // If they had selected a path that no longer exists, reset to null.
      // "default" always exists (synthetic entry), so only check custom paths.
      if (selectedAgentPath.value !== null && selectedAgentPath.value !== "default") {
        const stillExists = result.some((a) => a.path === selectedAgentPath.value);
        if (!stillExists) {
          selectedAgentPath.value = null;
        }
      }
    } catch {
      if (mine !== seq) return;
      agents.value = [];
    } finally {
      if (mine === seq) {
        loading.value = false;
      }
    }
  }

  /** True when there are multiple agents to choose from (default + at least one custom). */
  const hasChoice = computed(() => agents.value.length > 1);

  /** The display agent — either the user's selection or the first (default) agent. */
  const selectedAgent = computed<AvailableAgent | null>(() => {
    if (selectedAgentPath.value === null || selectedAgentPath.value === "default") {
      return agents.value.find((a) => a.isDefault) ?? agents.value[0] ?? null;
    }
    return agents.value.find((a) => a.path === selectedAgentPath.value) ?? null;
  });

  function selectAgent(path: string): void {
    selectedAgentPath.value = path;
  }

  // Re-fetch when context or site changes.
  watch(
    () => [toValue(siteId), toValue(contextPath)] as const,
    () => void fetchAgents(),
    { immediate: true },
  );

  return {
    agents,
    selectedAgent,
    selectedAgentPath,
    hasChoice,
    loading,
    selectAgent,
    fetchAgents,
  };
}
